/**
 * Acceptance Report Hooks
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import * as acceptanceService from '../api/services/acceptance.service';
import type {
  AcceptanceDetail,
  AcceptanceCreateRequest,
  AcceptanceUpdateRequest,
  AcceptanceSignInfo,
  AcceptanceTemplate,
  CompletedOrderDetail,
} from '../types';
import { toast } from '../stores/toast.store';

const KEYS = {
  templates: ['acceptance', 'templates'] as const,
  completedOrders: ['acceptance', 'completed-orders'] as const,
  completedOrder: (id: number) => ['acceptance', 'completed-orders', id] as const,
  acceptances: ['acceptance', 'list'] as const,
  acceptance: (id: number) => ['acceptance', 'detail', id] as const,
  signInfo: (token: string) => ['acceptance', 'sign', token] as const,
};

/**
 * Hook for fetching acceptance templates (for new acceptance form)
 */
export function useAcceptanceTemplates(enabled = true) {
  return useQuery<AcceptanceTemplate[]>({
    queryKey: KEYS.templates,
    queryFn: async () => {
      const result = await acceptanceService.getTemplates();
      if (!result.success) {
        throw new Error(result.error?.message || 'Failed to fetch templates');
      }
      return result.data!;
    },
    enabled,
    staleTime: 5 * 60 * 1000,
  });
}

interface UseCompletedOrdersParams {
  offset?: number;
  limit?: number;
  search?: string;
  myOrders?: boolean;
  excludeInvoiced?: boolean;
  hasCompletionDate?: boolean;
}

/**
 * Hook for fetching completed orders available for acceptance
 */
export function useCompletedOrders(params: UseCompletedOrdersParams = {}) {
  return useQuery({
    queryKey: [...KEYS.completedOrders, params],
    queryFn: async () => {
      const result = await acceptanceService.getCompletedOrders({
        offset: params.offset,
        limit: params.limit,
        search: params.search,
        my_orders: params.myOrders,
        exclude_invoiced: params.excludeInvoiced,
        has_completion_date: params.hasCompletionDate,
      });
      if (!result.success) {
        throw new Error(result.error?.message || 'Failed to fetch completed orders');
      }
      return result.data!;
    },
  });
}

/**
 * Hook for fetching completed order detail
 */
export function useCompletedOrderDetail(orderId: number | undefined) {
  return useQuery<CompletedOrderDetail>({
    queryKey: KEYS.completedOrder(orderId!),
    queryFn: async () => {
      const result = await acceptanceService.getCompletedOrderDetail(orderId!);
      if (!result.success) {
        throw new Error(result.error?.message || 'Failed to fetch order detail');
      }
      return result.data!;
    },
    enabled: !!orderId,
  });
}

interface UseAcceptanceListParams {
  offset?: number;
  limit?: number;
  state?: string;
  search?: string;
  myOrders?: boolean;
  unpaidOnly?: boolean;
  paidOnly?: boolean;
  invoicedDone?: boolean;
}

/**
 * Hook for fetching acceptance list
 */
export function useAcceptanceList(params: UseAcceptanceListParams = {}) {
  return useQuery({
    queryKey: [...KEYS.acceptances, params],
    queryFn: async () => {
      const result = await acceptanceService.getAcceptanceList({
        offset: params.offset,
        limit: params.limit,
        state: params.state,
        search: params.search,
        my_orders: params.myOrders,
        unpaid_only: params.unpaidOnly,
        paid_only: params.paidOnly,
        invoiced_done: params.invoicedDone,
      });
      if (!result.success) {
        throw new Error(result.error?.message || 'Failed to fetch acceptances');
      }
      return result.data!;
    },
  });
}

/**
 * Hook for fetching single acceptance detail
 */
export function useAcceptance(id: number | undefined) {
  return useQuery<AcceptanceDetail>({
    queryKey: KEYS.acceptance(id!),
    queryFn: async () => {
      const result = await acceptanceService.getAcceptance(id!);
      if (!result.success) {
        throw new Error(result.error?.message || 'Failed to fetch acceptance');
      }
      return result.data!;
    },
    enabled: !!id,
  });
}

/**
 * Hook for fetching public sign info
 */
export function useSignInfo(token: string | undefined) {
  return useQuery<AcceptanceSignInfo>({
    queryKey: KEYS.signInfo(token!),
    queryFn: async () => {
      const result = await acceptanceService.getSignInfo(token!);
      if (!result.success) {
        throw new Error(result.error?.message || 'Failed to fetch sign info');
      }
      return result.data!;
    },
    enabled: !!token,
  });
}

/**
 * Hook for creating acceptance
 */
export function useCreateAcceptance() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: AcceptanceCreateRequest) => {
      const result = await acceptanceService.createAcceptance(data);
      if (!result.success) {
        throw new Error(result.error?.message || 'Failed to create acceptance');
      }
      return result.data!;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: KEYS.completedOrders });
      queryClient.invalidateQueries({ queryKey: KEYS.acceptances });
      toast.success('Acceptance created');
    },
    onError: (error: Error) => {
      toast.error('Failed to create', error.message);
    },
  });
}

/**
 * Hook for updating acceptance
 */
export function useUpdateAcceptance() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      id,
      data,
    }: {
      id: number;
      data: AcceptanceUpdateRequest;
    }) => {
      const result = await acceptanceService.updateAcceptance(id, data);
      if (!result.success) {
        throw new Error(result.error?.message || 'Failed to update acceptance');
      }
      return result.data!;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: KEYS.acceptances });
      queryClient.setQueryData(KEYS.acceptance(data.id), data);
      toast.success('Updated');
    },
    onError: (error: Error) => {
      toast.error('Update failed', error.message);
    },
  });
}

/**
 * Hook for employee signing.
 * 業務單簽新流程：簽名即完成驗收（state 直接變 'signed'），可接著送出建立應收憑單。
 */
export function useEmployeeSign() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, signature }: { id: number; signature: string }) => {
      const result = await acceptanceService.employeeSign(id, signature);
      if (!result.success) {
        throw new Error(result.error?.message || 'Failed to sign');
      }
      return result.data!;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: KEYS.acceptance(data.id) });
      queryClient.invalidateQueries({ queryKey: KEYS.acceptances });
      toast.success('驗收完成', '已簽名完成驗收，可送出建立應收憑單');
    },
    onError: (error: Error) => {
      toast.error('簽名失敗', error.message);
    },
  });
}

/**
 * Hook for issuing e-invoice (for existing invoice without e-invoice)
 */
export function useIssueAcceptanceEinvoice() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: number) => {
      const result = await acceptanceService.issueAcceptanceEinvoice(id);
      if (!result.success) {
        throw new Error(result.error?.message || 'Failed to issue e-invoice');
      }
      return result.data!;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: KEYS.acceptances });
      queryClient.setQueryData(KEYS.acceptance(data.id), data);
      toast.success('電子發票已開立');
    },
    onError: (error: Error) => {
      toast.error('開立失敗', error.message);
    },
  });
}

/**
 * Hook for sending LINE notification to customer (Flex Message with package + einvoice links)
 * Backend: POST /api/saiens/acceptance/{id}/notify-line
 * Allowed: state in (invoiced, done) + partner LINE bound + invoice posted + einvoice issued
 */
export function useNotifyAcceptanceLine() {
  return useMutation({
    mutationFn: async (id: number) => {
      const result = await acceptanceService.notifyLineCustomer(id);
      if (!result.success) {
        throw new Error(result.error?.message || 'LINE 推送失敗');
      }
      return result.data!;
    },
    onSuccess: (data) => {
      const targetLabel = data.target === 'group' ? 'LINE 群組' : 'LINE 個人';
      toast.success('已發送', `已推送驗收完成通知至客戶${targetLabel}（${data.partner_name}）`);
    },
    onError: (error: Error) => {
      toast.error('LINE 推送失敗', error.message);
    },
  });
}

/**
 * Hook for uploading acceptance photos
 */
export function useUploadAcceptancePhotos() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      saleOrderId,
      images,
    }: {
      saleOrderId: number;
      images: string[];
    }) => {
      const result = await acceptanceService.uploadAcceptancePhotos(saleOrderId, images);
      if (!result.success) {
        throw new Error(result.error?.message || 'Failed to upload photos');
      }
      return result.data!;
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({
        queryKey: KEYS.completedOrder(variables.saleOrderId),
      });
      toast.success('照片上傳成功');
    },
    onError: (error: Error) => {
      toast.error('上傳失敗', error.message);
    },
  });
}

/**
 * Hook for requesting payment delay
 */
export function useRequestPaymentDelay() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      id,
      reason,
      delayDays,
    }: {
      id: number;
      reason: string;
      delayDays: number;
    }) => {
      const result = await acceptanceService.requestPaymentDelay(id, reason, delayDays);
      if (!result.success) {
        throw new Error(result.error?.message || 'Failed to request payment delay');
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: KEYS.acceptances });
      toast.success('延後付款申請已送出');
    },
    onError: (error: Error) => {
      toast.error('申請失敗', error.message);
    },
  });
}

/**
 * Hook for submitting acceptance
 */
export function useSubmitAcceptance() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: number) => {
      const result = await acceptanceService.submitAcceptance(id);
      if (!result.success) {
        throw new Error(result.error?.message || 'Failed to submit');
      }
      return result.data!;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: KEYS.acceptances });
      queryClient.setQueryData(KEYS.acceptance(data.id), data);
      toast.success('Submitted and invoice created');
    },
    onError: (error: Error) => {
      toast.error('Submit failed', error.message);
    },
  });
}

